import { inSubGroup, type NavigationEntry, type NavigationSubGroup } from "@positive/ui";
import "@ui5/webcomponents-icons/dist/add-product.js";
import "@ui5/webcomponents-icons/dist/bar-code.js";
import "@ui5/webcomponents-icons/dist/calendar.js";
import "@ui5/webcomponents-icons/dist/decrease-line-height.js";
import "@ui5/webcomponents-icons/dist/document.js";
import "@ui5/webcomponents-icons/dist/dimension.js";
import "@ui5/webcomponents-icons/dist/inspection.js";
import "@ui5/webcomponents-icons/dist/list.js";
import "@ui5/webcomponents-icons/dist/process.js";
import "@ui5/webcomponents-icons/dist/shipping-status.js";
import "@ui5/webcomponents-icons/dist/table-view.js";
import "@ui5/webcomponents-icons/dist/wrench.js";

import { documentKinds, kindPermission, listPaths, type DocumentKind } from "./kinds";
import { assetsPermissions } from "./permissions";

const kindSubGroup: Partial<Record<DocumentKind, NavigationSubGroup>> = {
  settlements: "followUp",
  impairments: "followUp",
  revaluations: "followUp",
};

const kindIcon: Record<DocumentKind, string> = {
  acquisitions: "add-product",
  settlements: "process",
  transfers: "shipping-status",
  sales: "document",
  scrappings: "decrease-line-height",
  impairments: "decrease-line-height",
  revaluations: "dimension",
};

const kindLabel: Record<DocumentKind, string> = {
  acquisitions: "nav.faAcquisitions",
  settlements: "nav.faSettlements",
  transfers: "nav.faTransfers",
  sales: "nav.faSales",
  scrappings: "nav.faScrappings",
  impairments: "nav.faImpairments",
  revaluations: "nav.faRevaluations",
};

/** Fixed Assets entries of the navigation registry (group `assets`, plan module `assets`). */
export const assetsNavigation: readonly NavigationEntry[] = [
  {
    key: "assets.register",
    labelKey: "nav.faRegister",
    icon: "list",
    path: "/assets/register",
    permission: assetsPermissions.assetView,
  },
  {
    key: "assets.classes",
    labelKey: "nav.faClasses",
    icon: "dimension",
    path: "/assets/classes",
    permission: assetsPermissions.classView,
  },
  ...documentKinds.map((kind) => ({
    key: `assets.${kind}`,
    ...inSubGroup(kindSubGroup[kind]),
    labelKey: kindLabel[kind],
    icon: kindIcon[kind],
    path: listPaths[kind],
    permission: kindPermission(kind, "view"),
  })),
  {
    key: "assets.depreciation",
    labelKey: "nav.faDepreciation",
    icon: "calendar",
    path: "/assets/depreciation",
    permission: assetsPermissions.depreciationView,
  },
  {
    key: "assets.leases",
    subGroup: "followUp",
    labelKey: "nav.faLeases",
    icon: "document",
    path: "/assets/leases",
    permission: assetsPermissions.leaseView,
  },
  {
    key: "assets.maintenance",
    subGroup: "followUp",
    labelKey: "nav.faMaintenance",
    icon: "wrench",
    path: "/assets/maintenance",
    permission: assetsPermissions.maintenanceView,
  },
  {
    key: "assets.counts",
    subGroup: "followUp",
    labelKey: "nav.faCounts",
    icon: "bar-code",
    path: "/assets/counts",
    permission: assetsPermissions.countView,
  },
  {
    key: "assets.report.register",
    subGroup: "reports",
    labelKey: "nav.faReportRegister",
    icon: "table-view",
    path: "/assets/reports/register",
    permission: assetsPermissions.registerView,
  },
  {
    key: "assets.report.rollForward",
    subGroup: "reports",
    labelKey: "nav.faReportRollForward",
    icon: "table-view",
    path: "/assets/reports/roll-forward",
    permission: assetsPermissions.rollForwardView,
  },
  {
    key: "assets.report.forecast",
    subGroup: "reports",
    labelKey: "nav.faReportForecast",
    icon: "table-view",
    path: "/assets/reports/depreciation-forecast",
    permission: assetsPermissions.forecastView,
  },
  {
    key: "assets.report.leaseMaturity",
    subGroup: "reports",
    labelKey: "nav.faReportLeaseMaturity",
    icon: "inspection",
    path: "/assets/reports/lease-maturity",
    permission: assetsPermissions.leaseMaturityView,
  },
];
